import { useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import { styled } from "styled-components";

import { CarouselSlider, ListItemBox, Tile } from "common/components";
import { mediaStyles } from "common/styles";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { applyEventsSearchCriteria } from "domains/events/actions";
import { createEventsForTopicSelector, getEventFilterCount, getIsEventsFiltered } from "domains/events/selectors";
import { TopicDetailSection } from "domains/topicDetail/components/TopicDetailSection";
import { getTmSelectedId } from "domains/transformationMap/selectors";
import { useMedia } from "domains/viewport/useMedia";
import { createEventsSelector } from "entities/events/selectors";

const StyledCarousel = styled(CarouselSlider)`
  padding: 0;
`;

const StyledCard = styled(ListItemBox)`
  padding: 1rem;
  ${mediaStyles(({ spacing }) => `margin-right: ${spacing}rem;`)}
`;

type EventSectionProps = { selectedSectionId?: string; topicId: string };

export const EventSection = ({ topicId, selectedSectionId }: EventSectionProps) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const numberOfTiles = useMedia({ small: 1 }, 2);
  const filterCount = useSelector(getEventFilterCount);
  const isFiltered = useSelector(getIsEventsFiltered);
  const selectedInnerNodeId = useSelector(getTmSelectedId);
  const translate = useTranslate();

  const id = selectedInnerNodeId ?? topicId;

  const eventsForTopic = useSelector(createEventsForTopicSelector(id));

  const eventsSelector = useMemo(() => createEventsSelector(eventsForTopic?.data ?? []), [eventsForTopic?.data]);

  const events = useSelector(eventsSelector);

  const isPending = eventsForTopic?.isFetching ?? false;
  const path = `${location.pathname}/events`;

  useEffect(() => {
    if (id && events.length === 0) {
      dispatch(applyEventsSearchCriteria({ offset: 0, limit: 20 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id]);

  if (events.length === 0 && !isPending && !isFiltered) {
    return null;
  }

  return (
    <TopicDetailSection
      display={events.length !== 0 || !isPending}
      filterCount={filterCount}
      isHidden={!!selectedSectionId}
      isHorizontal
      seeAllLink={path}
      title={translate("app.discover.topic.tabs.events.title")}
      topicId={topicId}
    >
      <StyledCarousel numberOfItems={numberOfTiles}>
        {events.slice(0, 5).map((event) => (
          <Tile key={event.id} title={event.name} image={event.images?.thumbnail} link={`${path}/${event.id}`} />
        ))}
      </StyledCarousel>
      {events.length === 0 && !isPending && isFiltered && (
        <StyledCard>{translate("app.monitor.empty_message_filter")}</StyledCard>
      )}
    </TopicDetailSection>
  );
};
